import { ArrowDownTray, Code, DocumentText, History } from '@medusajs/icons';
import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  IconButton,
  Prompt,
  Select,
  Skeleton,
  StatusBadge,
  Table,
  Text,
  toast,
} from '@pcrm/fe/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { SectionContainer, SectionEmpty, SectionHeader } from '../../components/section';
import { ApiError } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { useHasPermission } from '../auth/use-session';
import {
  billingMonthLabel,
  currentBillingMonth,
  invoiceFileUrl,
  shiftMonth,
} from '../billing/billing.api';
import {
  canQueue,
  E_FACTURA_QUERY_KEY,
  E_FACTURA_STATUS_COLORS,
  E_FACTURA_STATUS_LABELS,
  type EFacturaAttempt,
  type EFacturaCounts,
  type EFacturaRow,
  type EFacturaStatus,
  eFacturaAttemptsQueryOptions,
  eFacturaQueryOptions,
  eFacturaResponseUrl,
  queueEFactura,
} from './efactura.api';

/** `Select` nu acceptă valoare goală: aceeași valoare înseamnă „toate firmele" / „toate lunile". */
const ANY = '_toate';

/** Câte luni în urmă oferă selectorul lunii emiterii. */
const MONTHS_BACK = 24;

/**
 * Filtrul de stare, în cuvintele ecranului. Implicit: **tot ce nu e încă acceptat** — lista de
 * lucru, nu arhiva.
 */
export const E_FACTURA_FILTERS = [
  {
    value: 'de-rezolvat',
    label: 'De rezolvat',
    statuses: ['none', 'queued', 'uploading', 'processing', 'rejected', 'unclear'],
  },
  { value: 'nemarcate', label: 'Nemarcate', statuses: ['none'] },
  { value: 'pe-drum', label: 'De trimis și în prelucrare', statuses: ['queued', 'uploading', 'processing'] },
  { value: 'respinse', label: 'Respinse', statuses: ['rejected'] },
  { value: 'neclare', label: 'Neclare', statuses: ['unclear'] },
  { value: 'acceptate', label: 'Acceptate', statuses: ['accepted'] },
  {
    value: 'toate',
    label: 'Toate',
    statuses: ['none', 'queued', 'uploading', 'processing', 'accepted', 'rejected', 'unclear'],
  },
] as const satisfies readonly {
  value: string;
  label: string;
  statuses: readonly (EFacturaStatus | 'none')[];
}[];

export type EFacturaFilterValue = (typeof E_FACTURA_FILTERS)[number]['value'];

export interface EFacturaScreenProps {
  /** Ecranul din fișa firmei: firma e fixă, iar filtrul de firmă nu apare. */
  readonly fixedCompanyId?: string;
  readonly companyId: string | undefined;
  readonly filter: EFacturaFilterValue | undefined;
  /** Luna emiterii, `2026-10`; lipsă = toate lunile. */
  readonly month: string | undefined;
  readonly onCompanyChange?: (companyId: string | undefined) => void;
  readonly onFilterChange: (filter: EFacturaFilterValue | undefined) => void;
  readonly onMonthChange: (month: string | undefined) => void;
}

/**
 * e-Factura ([#240](https://github.com/bplugaru/efx-pcrm/issues/240)): facturile emise, cu starea
 * lor în SPV, eroarea ANAF când e una, și marcarea „de trimis".
 *
 * Fondatorul, 2026-10-22: *„un ecran unde vedem toate astea, general și pe fiecare firmă
 * separat; acolo se vede și eroarea"*. Același ecran stă în Conta (toate firmele) și în fișa
 * firmei (tabul e-Factura).
 *
 * **Omul marchează, jobul trimite.** O factură respinsă de ANAF **nu mai pleacă singură**: se
 * corectează din fișa clientului și se marchează din nou, de aici.
 */
export function EFacturaScreen(props: EFacturaScreenProps) {
  const { fixedCompanyId, filter, month } = props;
  const companyId = fixedCompanyId ?? props.companyId;
  const can = useHasPermission();
  const canIssue = can('invoices.issue');
  const queryClient = useQueryClient();
  const statuses = (E_FACTURA_FILTERS.find((item) => item.value === filter) ?? E_FACTURA_FILTERS[0])
    .statuses;
  const bounds = month === undefined ? null : monthBounds(month);
  const { data, isPending, error } = useQuery(
    eFacturaQueryOptions({
      companyId,
      statuses,
      from: bounds?.from,
      to: bounds?.to,
    }),
  );

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [history, setHistory] = useState<EFacturaRow | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: bifele țin de filtru, nu de altceva.
  useEffect(() => {
    setSelected(new Set());
  }, [companyId, filter, month]);

  const rows = data?.rows ?? [];
  const markable = rows.filter((row) => canQueue(row.status) && row.files.xml);
  const chosen = markable.filter((row) => selected.has(row.invoiceId));
  const allChosen = markable.length > 0 && chosen.length === markable.length;

  const toggle = (row: EFacturaRow, value: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (value) {
        next.add(row.invoiceId);
      } else {
        next.delete(row.invoiceId);
      }
      return next;
    });

  const queue = useMutation({
    mutationFn: () => queueEFactura(chosen.map((row) => row.invoiceId)),
    onSuccess: async (result) => {
      setConfirming(false);
      setSelected(new Set());

      if (result.queued > 0) {
        toast.success(
          result.queued === 1
            ? 'O factură marcată de trimis. Pleacă la ANAF în câteva secunde.'
            : `${result.queued} facturi marcate de trimis. Pleacă la ANAF în câteva minute.`,
        );
      }

      for (const skip of result.skipped) {
        toast.warning(`${skip.label}: ${skip.message}`);
      }

      await queryClient.invalidateQueries({ queryKey: E_FACTURA_QUERY_KEY });
    },
    onError: (failure: unknown) => {
      setConfirming(false);
      toast.error(failure instanceof ApiError ? failure.message : 'Facturile nu au putut fi marcate.');
    },
  });

  const unclearChosen = chosen.filter((row) => row.status === 'unclear').length;

  return (
    <SectionContainer>
      <SectionHeader
        title="e-Factura"
        subtitle="Facturile emise și starea lor în SPV. Ce marchezi pleacă la ANAF; o factură respinsă nu mai pleacă până n-o corectezi și n-o marchezi din nou."
      />

      <div className="flex flex-wrap items-end gap-3 px-6 py-4">
        {fixedCompanyId === undefined ? (
          <FilterField label="Firmă" htmlFor="efactura-firma">
            <Select
              value={companyId ?? ANY}
              onValueChange={(value) => props.onCompanyChange?.(value === ANY ? undefined : value)}
            >
              <Select.Trigger id="efactura-firma" className="w-56">
                <Select.Value placeholder="Toate firmele" />
              </Select.Trigger>
              <Select.Content className="z-50">
                <Select.Item value={ANY}>Toate firmele</Select.Item>
                {(data?.companies ?? []).map((company) => (
                  <Select.Item key={company.id} value={company.id}>
                    {company.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </FilterField>
        ) : null}

        <FilterField label="Stare în SPV" htmlFor="efactura-stare">
          <Select
            value={filter ?? E_FACTURA_FILTERS[0].value}
            onValueChange={(value) =>
              props.onFilterChange(
                value === E_FACTURA_FILTERS[0].value ? undefined : (value as EFacturaFilterValue),
              )
            }
          >
            <Select.Trigger id="efactura-stare" className="w-64">
              <Select.Value />
            </Select.Trigger>
            <Select.Content className="z-50">
              {E_FACTURA_FILTERS.map((item) => (
                <Select.Item key={item.value} value={item.value}>
                  {item.label}
                  {data ? ` (${countOf(data.counts, item.statuses)})` : ''}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </FilterField>

        <FilterField label="Luna emiterii" htmlFor="efactura-luna">
          <MonthSelect month={month} onChange={props.onMonthChange} />
        </FilterField>
      </div>

      {data && (data.counts.rejected > 0 || data.counts.unclear > 0) ? (
        <div className="flex flex-col gap-y-2 px-6 pb-3">
          {data.counts.rejected > 0 ? (
            <div className="flex items-start gap-x-2">
              <StatusBadge color="red">Respinse</StatusBadge>
              <Text size="small">
                {data.counts.rejected === 1
                  ? 'O factură respinsă de ANAF: corectați-o din fișa clientului, apoi marcați-o din nou.'
                  : `${data.counts.rejected} facturi respinse de ANAF: corectați-le din fișa clientului, apoi marcați-le din nou.`}
              </Text>
            </div>
          ) : null}
          {data.counts.unclear > 0 ? (
            <div className="flex items-start gap-x-2">
              <StatusBadge color="orange">Neclare</StatusBadge>
              <Text size="small">
                {data.counts.unclear === 1
                  ? 'O factură a plecat fără răspuns de la ANAF. Verificați în SPV dacă a ajuns înainte s-o marcați din nou.'
                  : `${data.counts.unclear} facturi au plecat fără răspuns de la ANAF. Verificați în SPV dacă au ajuns înainte să le marcați din nou.`}
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}

      {isPending ? (
        <div className="flex flex-col gap-3 px-6 py-4">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-5/6" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : error ? (
        <SectionEmpty tone="error">
          {error instanceof Error ? error.message : 'Lista nu a putut fi citită.'}
        </SectionEmpty>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
            <div className="flex items-center gap-x-3">
              {canIssue ? (
                <>
                  <Checkbox
                    id="efactura-bifeaza-tot"
                    checked={allChosen}
                    disabled={markable.length === 0}
                    onCheckedChange={(value) =>
                      setSelected(value === true ? new Set(markable.map((row) => row.invoiceId)) : new Set())
                    }
                    aria-label="Bifează tot"
                  />
                  <label htmlFor="efactura-bifeaza-tot" className="txt-compact-small-plus">
                    {allChosen ? 'Debifează tot' : 'Bifează tot'}
                  </label>
                </>
              ) : null}
              <Text size="small" className="text-ui-fg-subtle">
                {rows.length} {rows.length === 1 ? 'factură' : 'facturi'}
                {canIssue ? ` · ${chosen.length} bifate din ${markable.length} de marcat` : ''}
              </Text>
            </div>

            {canIssue ? (
              <Button
                size="small"
                disabled={chosen.length === 0 || queue.isPending}
                onClick={() => setConfirming(true)}
              >
                Marchează de trimis ({chosen.length})
              </Button>
            ) : null}
          </div>

          {data?.truncated ? (
            <Text size="small" className="px-6 pb-3 text-ui-fg-subtle">
              Se văd primele {data.limit}. Alegeți o lună sau o firmă ca să vedeți restul.
            </Text>
          ) : null}

          {rows.length === 0 ? (
            <SectionEmpty>Nicio factură pentru filtrele alese.</SectionEmpty>
          ) : (
            <EFacturaTable
              rows={rows}
              showCompany={fixedCompanyId === undefined}
              canIssue={canIssue}
              selected={selected}
              onToggle={toggle}
              onHistory={setHistory}
            />
          )}
        </>
      )}

      <Prompt open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>
              Trimiți {chosen.length === 1 ? 'o factură' : `${chosen.length} facturi`} la ANAF?
            </Prompt.Title>
            <Prompt.Description>
              Pleacă în SPV în câteva minute. De acolo nu se mai corectează și nu se mai șterg —
              decât dacă ANAF le respinge.
              {unclearChosen > 0
                ? ` ${unclearChosen === 1 ? 'Una e neclară' : `${unclearChosen} sunt neclare`}: retrimiteți-le doar dacă ați verificat în SPV că n-au ajuns, altfel apar de două ori.`
                : ''}
            </Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Renunță</Prompt.Cancel>
            <Prompt.Action
              onClick={(event) => {
                event.preventDefault();
                queue.mutate();
              }}
            >
              Marchează de trimis
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>

      <HistoryDrawer row={history} onClose={() => setHistory(null)} />
    </SectionContainer>
  );
}

function EFacturaTable({
  rows,
  showCompany,
  canIssue,
  selected,
  onToggle,
  onHistory,
}: {
  rows: readonly EFacturaRow[];
  showCompany: boolean;
  canIssue: boolean;
  selected: ReadonlySet<string>;
  onToggle: (row: EFacturaRow, value: boolean) => void;
  onHistory: (row: EFacturaRow) => void;
}) {
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          {canIssue ? <Table.HeaderCell className="w-[44px]" /> : null}
          <Table.HeaderCell>Factura</Table.HeaderCell>
          <Table.HeaderCell>Client</Table.HeaderCell>
          {showCompany ? <Table.HeaderCell>Firmă</Table.HeaderCell> : null}
          <Table.HeaderCell className="w-[120px] text-right">Total</Table.HeaderCell>
          <Table.HeaderCell>Stare în SPV</Table.HeaderCell>
          <Table.HeaderCell className="w-[130px]" />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row) => {
          const markable = canQueue(row.status) && row.files.xml;

          return (
            <Table.Row key={row.invoiceId} className="align-top">
              {canIssue ? (
                <Table.Cell>
                  {markable ? (
                    <Checkbox
                      checked={selected.has(row.invoiceId)}
                      onCheckedChange={(value) => onToggle(row, value === true)}
                      aria-label={`Bifează ${row.series} ${row.number}`}
                    />
                  ) : null}
                </Table.Cell>
              ) : null}
              <Table.Cell>
                <div className="flex flex-col gap-y-1 py-2">
                  <span className="font-medium">
                    {row.series} {row.number}
                  </span>
                  <Text size="small" className="text-ui-fg-subtle">
                    {formatDate(row.issueDate)}
                  </Text>
                </div>
              </Table.Cell>
              <Table.Cell>
                <Link
                  to="/clienti/$customerId"
                  params={{ customerId: row.customerId }}
                  search={{ tab: 'financiar' }}
                  className="text-ui-fg-interactive hover:underline"
                >
                  {row.buyerName || 'Client'}
                </Link>
              </Table.Cell>
              {showCompany ? (
                <Table.Cell className="text-ui-fg-subtle">{row.companyName || '-'}</Table.Cell>
              ) : null}
              <Table.Cell className="text-right">{formatMoney(row.payable)}</Table.Cell>
              <Table.Cell>
                <RowStatus row={row} />
              </Table.Cell>
              <Table.Cell>
                <div className="flex items-center justify-end gap-x-1 py-1">
                  {row.files.pdf ? (
                    <IconButton size="small" variant="transparent" asChild>
                      <a
                        href={invoiceFileUrl(row.invoiceId, 'pdf')}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`PDF ${row.series} ${row.number}`}
                      >
                        <DocumentText />
                      </a>
                    </IconButton>
                  ) : null}
                  {row.files.xml ? (
                    <IconButton size="small" variant="transparent" asChild>
                      <a
                        href={invoiceFileUrl(row.invoiceId, 'xml')}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`XML ${row.series} ${row.number}`}
                      >
                        <Code />
                      </a>
                    </IconButton>
                  ) : null}
                  {row.attempts > 0 ? (
                    <IconButton
                      size="small"
                      variant="transparent"
                      aria-label={`Istoricul trimiterilor ${row.series} ${row.number}`}
                      onClick={() => onHistory(row)}
                    >
                      <History />
                    </IconButton>
                  ) : null}
                </div>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
}

function RowStatus({ row }: { row: EFacturaRow }) {
  const status = row.status ?? 'none';
  const latest = row.latest;

  return (
    <div className="flex flex-col gap-y-1 py-2">
      <span className="flex flex-wrap items-center gap-x-2">
        <StatusBadge color={E_FACTURA_STATUS_COLORS[status]}>{E_FACTURA_STATUS_LABELS[status]}</StatusBadge>
        {latest?.environment === 'test' ? (
          <Badge size="2xsmall" color="orange">
            ANAF test
          </Badge>
        ) : null}
      </span>

      {latest?.uploadIndex && (status === 'processing' || status === 'accepted') ? (
        <Text size="xsmall" className="text-ui-fg-muted">
          index {latest.uploadIndex}
        </Text>
      ) : null}

      {row.status === null && !row.files.xml ? (
        <Text size="xsmall" className="text-ui-fg-muted">
          XML-ul se face; se poate marca peste câteva secunde.
        </Text>
      ) : null}

      {latest?.error ? (
        <Text
          size="xsmall"
          className={`whitespace-pre-line ${status === 'rejected' ? 'text-ui-fg-error' : 'text-ui-fg-subtle'}`}
        >
          {status === 'queued' || status === 'processing' ? `Se reîncearcă: ${latest.error}` : latest.error}
        </Text>
      ) : null}

      {status === 'rejected' && row.editable ? (
        <Link
          to="/clienti/$customerId"
          params={{ customerId: row.customerId }}
          search={{ tab: 'financiar' }}
          className="txt-compact-xsmall text-ui-fg-interactive hover:underline"
        >
          Corectează factura
        </Link>
      ) : null}
    </div>
  );
}

/** Istoria încercărilor unei facturi: cine a marcat-o, ce a spus ANAF, arhiva răspunsului. */
function HistoryDrawer({ row, onClose }: { row: EFacturaRow | null; onClose: () => void }) {
  const { data, isPending, error } = useQuery({
    ...eFacturaAttemptsQueryOptions(row?.invoiceId ?? ''),
    enabled: row !== null,
  });

  return (
    <Drawer open={row !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{row ? `Trimiterile facturii ${row.series} ${row.number}` : ''}</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          {isPending ? (
            <Skeleton className="h-5 w-full" />
          ) : error ? (
            <Text size="small" className="text-ui-fg-error">
              {error instanceof Error ? error.message : 'Istoricul nu a putut fi citit.'}
            </Text>
          ) : (
            (data ?? []).map((attempt, index) => (
              <AttemptCard key={attempt.id} attempt={attempt} ordinal={(data?.length ?? 0) - index} />
            ))
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  );
}

function AttemptCard({ attempt, ordinal }: { attempt: EFacturaAttempt; ordinal: number }) {
  const facts: { label: string; value: string }[] = [
    { label: 'Marcată', value: `${formatDateTime(attempt.queuedAt)}, de ${attempt.queuedBy}` },
  ];

  if (attempt.environment !== null) {
    facts.push({ label: 'Mediul ANAF', value: attempt.environment === 'prod' ? 'producție' : 'test' });
  }

  if (attempt.uploadedAt !== null) {
    facts.push({ label: 'Trimisă', value: formatDateTime(attempt.uploadedAt) });
  }

  if (attempt.uploadIndex !== null) {
    facts.push({ label: 'Index de încărcare', value: attempt.uploadIndex });
  }

  if (attempt.checks > 0) {
    facts.push({
      label: 'Verificări',
      value: `${attempt.checks}${attempt.checkedAt ? `, ultima ${formatDateTime(attempt.checkedAt)}` : ''}`,
    });
  }

  if (attempt.anafState !== null) {
    facts.push({ label: 'Răspuns ANAF', value: attempt.anafState });
  }

  if (attempt.finishedAt !== null) {
    facts.push({ label: 'Încheiată', value: formatDateTime(attempt.finishedAt) });
  } else if (attempt.nextAttemptAt !== null) {
    facts.push({ label: 'Pasul următor', value: formatDateTime(attempt.nextAttemptAt) });
  }

  return (
    <div className="flex flex-col gap-y-2 rounded-md border border-ui-border-base p-3">
      <span className="flex items-center gap-x-2">
        <Text size="small" weight="plus">
          Încercarea {ordinal}
        </Text>
        <StatusBadge color={E_FACTURA_STATUS_COLORS[attempt.status]}>
          {E_FACTURA_STATUS_LABELS[attempt.status]}
        </StatusBadge>
      </span>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {facts.map((fact) => (
          <div key={fact.label} className="contents">
            <dt className="txt-compact-xsmall text-ui-fg-subtle">{fact.label}</dt>
            <dd className="txt-compact-xsmall">{fact.value}</dd>
          </div>
        ))}
      </dl>
      {attempt.error ? (
        <Text
          size="xsmall"
          className={`whitespace-pre-line ${attempt.status === 'rejected' ? 'text-ui-fg-error' : 'text-ui-fg-subtle'}`}
        >
          {attempt.error}
        </Text>
      ) : null}
      {attempt.hasResponse ? (
        <Button size="small" variant="secondary" asChild>
          <a href={eFacturaResponseUrl(attempt.id)}>
            <ArrowDownTray />
            {attempt.status === 'accepted' ? 'Factura semnată de MF (.zip)' : 'Răspunsul ANAF (.zip)'}
          </a>
        </Button>
      ) : null}
    </div>
  );
}

function MonthSelect({
  month,
  onChange,
}: {
  month: string | undefined;
  onChange: (month: string | undefined) => void;
}) {
  const current = currentBillingMonth();
  const options = useMemo(
    () => Array.from({ length: MONTHS_BACK + 1 }, (_, index) => shiftMonth(current, -index)),
    [current],
  );

  return (
    <Select value={month ?? ANY} onValueChange={(value) => onChange(value === ANY ? undefined : value)}>
      <Select.Trigger id="efactura-luna" className="w-56">
        <Select.Value placeholder="Toate lunile" />
      </Select.Trigger>
      <Select.Content className="z-50">
        <Select.Item value={ANY}>Toate lunile</Select.Item>
        {(options.includes(month ?? '') || month === undefined ? options : [month, ...options]).map(
          (option) => (
            <Select.Item key={option} value={option}>
              {billingMonthLabel(option)}
            </Select.Item>
          ),
        )}
      </Select.Content>
    </Select>
  );
}

function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-y-1">
      <label htmlFor={htmlFor} className="txt-compact-xsmall-plus text-ui-fg-subtle">
        {label}
      </label>
      {children}
    </div>
  );
}

function countOf(counts: EFacturaCounts, statuses: readonly (EFacturaStatus | 'none')[]): number {
  return statuses.reduce((total, status) => total + counts[status], 0);
}

/** Prima și ultima zi a lunii: `2026-02` → `2026-02-01` … `2026-02-28`. */
export function monthBounds(month: string): { from: string; to: string } {
  const [year, monthNumber] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();

  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}
